import { Fragment } from 'react';

import type { StyleWithVars } from './types.js';
import type { ReactElement } from 'react';

type CharRevealProps = {
  readonly text: string;
  readonly delayMs?: number;
};

type PlacedWord = {
  readonly word: string;
  readonly from: number;
};

/**
 * `from` is the running character index across the whole string with spaces left out, so the
 * per-character stagger reads as one wave even though the markup is word-boxed for wrapping.
 */
const placeWords = (text: string): readonly PlacedWord[] => {
  const words = text.split(' ');

  return words.map((word, index) => ({ word, from: words.slice(0, index).join('').length }));
};

/** Drops a line in per character. Words stay unbreakable boxes so the text still wraps sanely. */
export const CharReveal = ({ text, delayMs = 0 }: CharRevealProps): ReactElement => {
  const words = placeWords(text);
  const style: StyleWithVars = { '--d': String(delayMs) };

  return (
    <span className="chars" aria-label={text} style={style}>
      {words.map(({ word, from }, wordIndex) => (
        <Fragment key={`${String(from)}-${word}`}>
          <span className="w" aria-hidden="true">
            {Array.from(word, (char, charIndex) => {
              const charStyle: StyleWithVars = { '--i': String(from + charIndex) };

              return (
                <span key={`${String(from + charIndex)}-${char}`} style={charStyle}>
                  {char}
                </span>
              );
            })}
          </span>
          {wordIndex < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </span>
  );
};
